/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import Image from '@/components/NextImage';
import { useLabels } from '@/hooks/useLabels';
import ThreeSixtyIcon from '@/public/images/360_Icon.svg';
import PlayArrowIcon from '@/public/images/play-arrow.svg';
import azCommonStyles from '@/theme/globals.module.scss';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import Skeleton from '../../Skeleton/Skeleton';
import { MAX_VISIBLE_THUMBNAILS } from '../constants';
import styles from './styles.module.scss';
import ThumbnailOverlay from './ThumbnailOverlay';
import type { CarouselItem, CarouselItemTypes, ThumbnailProps } from './types';
import { defaultVideoThumbnail } from '@/constants/images/defaultVideoThumbnail';
import { Button } from '@/components/Button';

const labelMap = {
  seeMoreLabel: 'hyperlink_label_shelf_See_More',
  imagesHeader: 'label_Thumbnails_header_Images',
  videoHeader: 'label_Thumbnails_header_Videos',
  previousThumbnailsLabel: 'label_previous_thumbnails',
  nextThumbnailsLabel: 'label_next_thumbnails',
};

const ThumbnailComponent = (props: ThumbnailProps) => {
  const {
    classes = {},
    verticalLayout,
    handleSeeMoreClick,
    handleThumbnailClick,
    groupedLayout,
    showAllThumbnails,
    selectedThumbnailIndex,
    snapToThumbnails,
    hoverEnabled,
    loading,
    onImageChange,
    items = [],
    imgTitle,
    showArrows,
    scrollAmount,
    leftArrowImage,
    rightArrowImage,
    arrowClasses = {},
  } = props;
  const { seeMoreLabel, imagesHeader, videoHeader, previousThumbnailsLabel, nextThumbnailsLabel } =
    useLabels(labelMap);

  const thumbnailListRef = useRef<HTMLElement | null>(null);
  const thumbnailRefs = useRef<Record<number, HTMLElement | null>>({});
  const justMounted = useRef(true);
  const [selectedThumbnail, setSelectedThumbnail] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { thumbnailGroup: thumbnailGroupClass = '', thumbnailList: thumbnailListClass } = classes;
  const thumbnails = items;
  const totalThumbnailLength = thumbnails.length;

  const getThumbnailsByType = (type: CarouselItemTypes): CarouselItem[] => {
    return thumbnails.filter((thumbnail: CarouselItem) => thumbnail.type === type);
  };

  const imageThumbnails = getThumbnailsByType('image');
  const getTrimmedThumbnails: () => CarouselItem[] = () => {
    return verticalLayout && !showAllThumbnails
      ? thumbnails.slice(0, MAX_VISIBLE_THUMBNAILS)
      : thumbnails;
  };

  const generateAccessibleLabel = (
    thumbnailItem: CarouselItem,
    thumbIndex: number,
    isSeeMore = false,
  ): string => {
    if (isSeeMore) {
      return seeMoreLabel;
    }

    const { type, alt, title } = thumbnailItem;
    const productName = imgTitle || title || alt || 'product';
    const position = thumbIndex + 1;

    switch (type) {
      case 'video':
        return `Play ${productName} video ${position}`;
      case 'spin360':
        return `View ${productName} 360-degree image ${position}`;
      case 'image':
      default:
        return `View ${productName} image ${position}`;
    }
  };

  const showExpandedImg = (carouselItem: CarouselItem) => {
    const { enlargedMediaUrl } = carouselItem;
    const index = thumbnails.findIndex((img) => img.enlargedMediaUrl === enlargedMediaUrl);

    if (onImageChange) {
      onImageChange(index, carouselItem);
    }

    setSelectedThumbnail(index);
  };

  const handleClick = (thumbnailItem: CarouselItem) => {
    showExpandedImg(thumbnailItem);
    handleThumbnailClick?.(thumbnailItem);
  };

  const handleScrollLeft = () => {
    if (thumbnailListRef.current) {
      const scroll = scrollAmount ?? 78;
      thumbnailListRef.current.scrollBy({
        left: -scroll,
        behavior: 'smooth',
      });
    }
  };

  const handleScrollRight = () => {
    if (thumbnailListRef.current) {
      const scroll = scrollAmount ?? 78;
      thumbnailListRef.current.scrollBy({
        left: scroll,
        behavior: 'smooth',
      });
    }
  };

  const updateArrowVisibility = () => {
    if (thumbnailListRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = thumbnailListRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    if (
      !!thumbnailRefs &&
      !!thumbnailListRef &&
      snapToThumbnails &&
      selectedThumbnailIndex !== undefined
    ) {
      const thumbnailRef = thumbnailRefs.current[selectedThumbnailIndex];
      const listRef = thumbnailListRef.current;
      if (thumbnailRef && listRef) {
        const thumbnailOffsetLeft = thumbnailRef.offsetLeft;
        const thumbnailWidth = thumbnailRef.offsetWidth;
        const thumbnailListCenter = listRef.offsetWidth / 2;
        const thumbnailFromLeftDistance = thumbnailOffsetLeft + thumbnailWidth / 2;
        const toCenter = thumbnailFromLeftDistance - thumbnailListCenter;
        const scrollBehavior = justMounted.current ? 'auto' : 'smooth';
        if (listRef.scroll) {
          listRef.scroll({
            left: toCenter,
            top: 0,
            behavior: scrollBehavior,
          });
        }
      }
      justMounted.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThumbnailIndex]);

  useEffect(() => {
    const listRef = thumbnailListRef.current;
    if (showArrows && listRef) {
      updateArrowVisibility();
      listRef.addEventListener('scroll', updateArrowVisibility);
      window.addEventListener('resize', updateArrowVisibility);

      return () => {
        listRef.removeEventListener('scroll', updateArrowVisibility);
        window.removeEventListener('resize', updateArrowVisibility);
      };
    }
  }, [showArrows, items]);

  const thumbnailStyle = verticalLayout
    ? styles.thumbnailBox
    : `${styles.thumbnailBox} ${styles.inlineThumbnailBox}`;
  const thumbnailContainerStyle = verticalLayout
    ? styles.thumbnailsContainer
    : `${styles.thumbnailsContainer} ${styles.thumbnailsContainerHorizontal}`;
  const shouldShowSeeMore =
    verticalLayout && !showAllThumbnails && totalThumbnailLength >= MAX_VISIBLE_THUMBNAILS;

  const renderThumbnail = (thumbnailItem: CarouselItem, thumbIndex: number) => {
    const { type, thumbnail } = thumbnailItem;
    const isVideo = type === 'video';
    const is360 = type === 'spin360';
    const isLastVisible = thumbIndex + 1 === MAX_VISIBLE_THUMBNAILS;
    const isSeeMoreButton = isLastVisible && shouldShowSeeMore;
    const actualThumbnailIndex =
      isVideo && groupedLayout ? thumbIndex + imageThumbnails.length : thumbIndex;
    const isInvalidVideoThumbnail = isVideo && (!thumbnail || thumbnail === 'null');

    const onMouseEnter =
      hoverEnabled && !isSeeMoreButton
        ? () => {
            showExpandedImg(thumbnailItem);
          }
        : () => {};
    const onClick = isSeeMoreButton
      ? () => handleSeeMoreClick?.()
      : () => handleClick(thumbnailItem);
    return (
      <li
        // @ts-expect-error fix type
        ref={(element) => (thumbnailRefs.current[actualThumbnailIndex] = element)}
        className={thumbnailStyle}
        key={`${thumbnail}-${thumbIndex}`}
      >
        <button
          className={cx(styles.thumbnailButton, {
            [styles.thumbnailButtonVideo]: isVideo,
            [styles.thumbnailButtonSelected]:
              !isSeeMoreButton &&
              (selectedThumbnailIndex !== undefined
                ? selectedThumbnailIndex === actualThumbnailIndex
                : selectedThumbnail === actualThumbnailIndex),
          })}
          onMouseEnter={onMouseEnter}
          onClick={onClick}
          aria-label={generateAccessibleLabel(thumbnailItem, thumbIndex, isSeeMoreButton)}
        >
          <>
            <Image
              src={isInvalidVideoThumbnail ? defaultVideoThumbnail.src : thumbnail}
              alt=""
              data-testid={`thumbnail-image-${thumbIndex}`}
              className={styles.thumbnailImage}
              width={70}
              height={70}
              itemProp="image"
              loading="lazy"
            />
            {(isVideo || is360) && !isSeeMoreButton && (
              <ThumbnailOverlay
                image={
                  isVideo
                    ? (PlayArrowIcon as React.ElementType)
                    : (ThreeSixtyIcon as React.ElementType)
                }
              />
            )}
            {isSeeMoreButton && <ThumbnailOverlay text={seeMoreLabel} />}
          </>
        </button>
      </li>
    );
  };

  const renderThumbnailList = (thumbnails: CarouselItem[]) => {
    return (
      <ul
        // @ts-expect-error fix type
        ref={thumbnailListRef}
        className={cx(thumbnailContainerStyle, thumbnailListClass, {
          [classes.thumbnailContainer || '']: classes.thumbnailContainer,
          [styles.thumbnailArrowsContainer]: showArrows && !verticalLayout,
        })}
        data-testid="thumbnails-container"
      >
        {!loading && thumbnails?.map(renderThumbnail)}
        {loading &&
          thumbnails?.map((_, index) => (
            <li key={index} className={thumbnailStyle}>
              <Skeleton
                animation="wave"
                height={70}
                width={70}
                variant="rect"
                data-testid="skeleton-loader"
              />
            </li>
          ))}
      </ul>
    );
  };

  const renderThumbnailGroup = (thumbnails: CarouselItem[] = [], header: string) => {
    return (
      <div className={styles.thumbnailGroup}>
        {!!header && !!thumbnails.length && (
          <div className={cx(styles.thumbnailHeading, azCommonStyles['az-title-5-medium'])}>
            {header}
          </div>
        )}
        {renderThumbnailList(thumbnails)}
      </div>
    );
  };

  const renderLeftArrow = () => {
    if (!canScrollLeft) {
      return null;
    }

    return (
      <Button
        customClass={cx(
          styles.thumbnailArrowControl,
          styles.thumbnailArrowControlLeft,
          arrowClasses?.arrowControl,
          arrowClasses?.arrowControlLeft,
        )}
        onClick={handleScrollLeft}
        ariaLabel={previousThumbnailsLabel}
      >
        <div className={styles.arrowImage}>
          <Image
            src={leftArrowImage ?? '/images/arrow-left-grey-shadow-white.svg'}
            alt=""
            width={15}
            height={27}
            loading="lazy"
          />
        </div>
      </Button>
    );
  };

  const renderRightArrow = () => {
    if (!canScrollRight) {
      return null;
    }

    return (
      <Button
        customClass={cx(
          styles.thumbnailArrowControl,
          styles.thumbnailArrowControlRight,
          arrowClasses?.arrowControl,
          arrowClasses?.arrowControlRight,
        )}
        onClick={handleScrollRight}
        ariaLabel={nextThumbnailsLabel}
      >
        <div className={styles.arrowImage}>
          <Image
            src={rightArrowImage ?? '/images/arrow-right-grey-shadow-white.svg'}
            alt=""
            width={15}
            height={27}
            loading="lazy"
          />
        </div>
      </Button>
    );
  };

  if (groupedLayout) {
    const videoThumbnails = getThumbnailsByType('video');
    return (
      <div className={thumbnailGroupClass}>
        {renderThumbnailGroup(imageThumbnails, imagesHeader)}
        {renderThumbnailGroup(videoThumbnails, videoHeader)}
      </div>
    );
  }

  if (showArrows && !verticalLayout) {
    return (
      <div className={styles.thumbnailsWithArrows}>
        {renderLeftArrow()}
        {renderThumbnailList(getTrimmedThumbnails())}
        {renderRightArrow()}
      </div>
    );
  }

  return renderThumbnailList(getTrimmedThumbnails());
};

export default ThumbnailComponent;
